import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PPCResults } from '@/types/primaryLedger';
import { SummaryCard } from './SummaryCard';
import { TrendingUp, MousePointerClick, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';

interface ColumnConfig {
  id: string;
  label: string;
  accessor: (item: PPCResults) => string;
}

interface PPCResultsSectionProps {
  data: PPCResults[];
  loading: boolean;
}

export function PPCResultsSection({ data, loading }: PPCResultsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [visibleSeries, setVisibleSeries] = useState({
    spend: true,
    sales: true,
    acos: true
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (timestamp: any) => {
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const defaultColumns: ColumnConfig[] = [
    { id: 'date', label: 'Date', accessor: (item) => formatDate(item.date) },
    { id: 'impressions', label: 'Impressions', accessor: (item) => formatNumber(item.impressions) },
    { id: 'clicks', label: 'Clicks', accessor: (item) => formatNumber(item.clicks) },
    { id: 'ctr', label: 'CTR', accessor: (item) => formatPercent(item.ctr) },
    { id: 'cpc', label: 'CPC', accessor: (item) => formatCurrency(item.cpc) },
    { id: 'spend', label: 'Spend', accessor: (item) => formatCurrency(item.spend) },
    { id: 'sales', label: 'Sales', accessor: (item) => formatCurrency(item.sales) },
    { id: 'acos', label: 'ACoS', accessor: (item) => formatPercent(item.acos) },
    { id: 'roas', label: 'ROAS', accessor: (item) => `${item.roas.toFixed(2)}x` },
    { id: 'orders', label: 'Orders', accessor: (item) => formatNumber(item.orders) },
  ];

  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);

  useEffect(() => {
    const saved = localStorage.getItem('ppc-results-columns');
    if (saved) {
      try {
        const savedColumns = JSON.parse(saved);
        setColumns(savedColumns);
      } catch (e) {
        console.error('Failed to parse saved columns:', e);
      }
    }
  }, []);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggedColumn, 1);
    newColumns.splice(dropIndex, 0, removed);
    
    setColumns(newColumns);
    localStorage.setItem('ppc-results-columns', JSON.stringify(newColumns));
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleToggleSeries = (dataKey: 'spend' | 'sales' | 'acos') => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const CustomLegend = () => {
    const seriesConfig = [
      { key: 'spend' as const, label: 'Spend', color: '#ef4444' },
      { key: 'sales' as const, label: 'Sales', color: '#10b981' },
      { key: 'acos' as const, label: 'ACoS', color: '#f59e0b' }
    ];

    return (
      <div className="flex justify-center gap-4 mt-4">
        {seriesConfig.map((series) => {
          const isVisible = visibleSeries[series.key];
          return (
            <button
              key={series.key}
              onClick={() => handleToggleSeries(series.key)}
              className={`flex items-center gap-2 px-3 py-1 rounded transition-all hover:bg-gray-100 dark:hover:bg-gray-800 ${
                isVisible ? 'opacity-100' : 'opacity-50'
              }`}
              title={isVisible ? 'Click to hide' : 'Click to show'}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: series.color }}
              />
              <span className={`text-sm text-gray-700 dark:text-gray-300 ${!isVisible ? 'line-through' : ''}`}>
                {series.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm text-gray-600 dark:text-gray-400">
              <span style={{ color: entry.color }}>{entry.name}: </span>
              {entry.name === 'ACoS' ? formatPercent(entry.value) : formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const calculateSummaryStats = () => {
    if (data.length === 0) {
      return {
        totalSpend: 0,
        avgAcos: 0,
        totalRoas: 0,
        totalImpressions: 0,
        totalClicks: 0,
        avgCtr: 0
      };
    }

    const totalSpend = data.reduce((sum, item) => sum + item.spend, 0);
    const totalSales = data.reduce((sum, item) => sum + item.sales, 0);
    const totalImpressions = data.reduce((sum, item) => sum + item.impressions, 0);
    const totalClicks = data.reduce((sum, item) => sum + item.clicks, 0);
    const avgAcos = data.reduce((sum, item) => sum + item.acos, 0) / data.length;
    const avgCtr = data.reduce((sum, item) => sum + item.ctr, 0) / data.length;
    const totalRoas = totalSpend > 0 ? totalSales / totalSpend : 0;

    return {
      totalSpend,
      avgAcos,
      totalRoas,
      totalImpressions,
      totalClicks,
      avgCtr
    };
  };

  const stats = calculateSummaryStats();

  const chartData = [...data]
    .sort((a, b) => {
      const dateA = a.date?.toDate?.() ?? new Date(0);
      const dateB = b.date?.toDate?.() ?? new Date(0);
      return dateA.getTime() - dateB.getTime();
    })
    .map(item => ({
      date: formatDate(item.date),
      spend: item.spend,
      sales: item.sales,
      acos: item.acos
    }));

  const sortedData = [...data].sort((a, b) => {
    const dateA = a.date?.toDate?.() ?? new Date(0);
    const dateB = b.date?.toDate?.() ?? new Date(0);
    return dateB.getTime() - dateA.getTime();
  });

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-500 dark:text-gray-400">Loading PPC results...</div>
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">PPC Results</h2>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-500 dark:text-gray-400">No PPC data available</div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Total Spend"
          value={formatCurrency(stats.totalSpend)}
          icon={DollarSign}
          color="blue"
        />
        <SummaryCard
          title="Avg ACoS"
          value={formatPercent(stats.avgAcos)}
          icon={TrendingUp}
          color="green"
          subtitle={`ROAS: ${stats.totalRoas.toFixed(2)}x`}
        />
        <SummaryCard
          title="Total Clicks"
          value={formatNumber(stats.totalClicks)}
          icon={MousePointerClick}
          color="purple"
          subtitle={`CTR: ${formatPercent(stats.avgCtr)}`}
        />
      </div>

      <Card>
        <div className="p-6">
          <h3 className="text-lg font-medium mb-4 dark:text-white">Performance Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-700" />
              <XAxis
                dataKey="date"
                className="text-xs dark:text-gray-400"
              />
              <YAxis
                yAxisId="left"
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                className="text-xs dark:text-gray-400"
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) => `${value.toFixed(0)}%`}
                className="text-xs dark:text-gray-400"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              {visibleSeries.spend && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="spend"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Spend"
                />
              )}
              {visibleSeries.sales && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="sales"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Sales"
                />
              )}
              {visibleSeries.acos && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="acos"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="ACoS"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card>
        <div
          className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold dark:text-white">PPC Results</h2>
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
        {isExpanded && (
          <div className="px-6 pb-6">
            <div className="overflow-auto max-h-96">
              <table className="w-full text-sm relative">
                <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
                  <tr className="border-b dark:border-gray-700">
                    {columns.map((col, index) => (
                      <th
                        key={col.id}
                        className={`p-2 cursor-move select-none ${index === 0 ? 'text-left' : 'text-right'} ${
                          draggedColumn === index ? 'opacity-50' : ''
                        }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, rowIndex) => (
                    <tr
                      key={item.id}
                      className={`border-b dark:border-gray-800 ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}`}
                    >
                      {columns.map((col, colIndex) => (
                        <td
                          key={col.id}
                          className={`p-2 ${colIndex === 0 ? 'text-left' : 'text-right'}`}
                        >
                          {col.accessor(item)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}